import * as React from "react";
import moment from "moment-timezone";
import { Button, DateRangePicker } from "@lib/components";

export type BookingDatePreset = "today" | "tomorrow" | "next7" | "all" | "custom";

/**
 * Virtual sort key understood by POST /dashboard/bookings: bookings whose time has not
 * passed first (soonest → latest), then passed bookings chronologically.
 */
export const UPCOMING_SORT_KEY = "upcoming";

type Region = T.Schema.Restaurant.RestaurantSchema["settings"]["region"];

export function bookingDateRangeForPreset(
	preset: BookingDatePreset,
	timezone: string,
	custom?: { start?: number; end?: number }
): { start?: number; end?: number } {
	const now = moment.tz(timezone);

	switch (preset) {
		case "today":
			return {
				start: now.clone().startOf("day").valueOf(),
				end: now.clone().endOf("day").valueOf(),
			};
		case "tomorrow": {
			const day = now.clone().add(1, "day");
			return {
				start: day.clone().startOf("day").valueOf(),
				end: day.clone().endOf("day").valueOf(),
			};
		}
		case "next7":
			return {
				start: now.clone().startOf("day").valueOf(),
				end: now.clone().add(6, "days").endOf("day").valueOf(),
			};
		case "custom":
			if (custom?.start && custom?.end) {
				return { start: custom.start, end: custom.end };
			}
			return bookingDateRangeForPreset("today", timezone);
		case "all":
		default:
			return {};
	}
}

/** Wide range for CSV export when All Time is selected (API requires start/end). */
export function bookingExportAllTimeRange(timezone: string): {
	start: number;
	end: number;
} {
	const now = moment.tz(timezone);
	return {
		start: now.clone().subtract(5, "years").startOf("day").valueOf(),
		end: now.clone().add(5, "years").endOf("day").valueOf(),
	};
}

export type BookingsDateFilter = { start: number; end: number };

/** True when no date filter is active, or booking timestamp falls within the filter. */
export function bookingMatchesDateFilter(
	bookingTimestamp: number,
	dateFilter?: BookingsDateFilter
): boolean {
	if (!dateFilter) return true;
	return (
		bookingTimestamp >= dateFilter.start &&
		bookingTimestamp <= dateFilter.end
	);
}

export function isBookingToday(
	bookingTimestamp: number,
	timezone: string
): boolean {
	const { start, end } = bookingDateRangeForPreset("today", timezone);
	if (start == null || end == null) return false;
	return bookingTimestamp >= start && bookingTimestamp <= end;
}

export type BookingsListSort = {
	sortKey: string;
	sortDirection: 1 | -1;
};

type BookingSortable = {
	created: number;
	config: { timestamp: number };
	customer: { name: string };
};

function compareBookingField(
	a: BookingSortable,
	b: BookingSortable,
	sortKey: string
): number {
	if (sortKey === "config.timestamp") {
		const cmp = a.config.timestamp - b.config.timestamp;
		if (cmp !== 0) return cmp;
		return a.created - b.created;
	}
	if (sortKey === "created") {
		return a.created - b.created;
	}
	if (sortKey === "customer.name") {
		return (a.customer.name || "").localeCompare(b.customer.name || "");
	}
	return 0;
}

/** All Time list: today's bookings first, then the rest by booking timestamp. */
export function sortBookingsTodayFirst<
	T extends BookingSortable
>(
	items: T[],
	timezone: string,
	timestampDirection: 1 | -1 = -1
): T[] {
	const { start, end } = bookingDateRangeForPreset("today", timezone);
	if (start == null || end == null) return items;

	return [...items].sort((a, b) => {
		const aToday =
			a.config.timestamp >= start && a.config.timestamp <= end ? 1 : 0;
		const bToday =
			b.config.timestamp >= start && b.config.timestamp <= end ? 1 : 0;
		if (aToday !== bToday) return bToday - aToday;
		const cmp = compareBookingField(a, b, "config.timestamp");
		return timestampDirection === -1 ? -cmp : cmp;
	});
}

/**
 * Upcoming First: bookings at/after `now` soonest → latest, then passed bookings in
 * chronological order (at 4 PM with 12/2/5/7 PM bookings → 5, 7, 12, 2).
 */
export function sortBookingsUpcomingFirst<T extends BookingSortable>(
	items: T[],
	now: number = Date.now()
): T[] {
	return [...items].sort((a, b) => {
		const aUp = a.config.timestamp >= now ? 1 : 0;
		const bUp = b.config.timestamp >= now ? 1 : 0;
		if (aUp !== bUp) return bUp - aUp;
		return compareBookingField(a, b, "config.timestamp");
	});
}

/** Client-side list order matching API sort (incl. today-first when All Time). */
export function sortBookingsList<T extends BookingSortable>(
	items: T[],
	options: {
		sortKey: string;
		sortDirection: 1 | -1;
		timezone?: string;
		dateFilter?: BookingsDateFilter;
	}
): T[] {
	const { sortKey, sortDirection, timezone, dateFilter } = options;

	if (sortKey === UPCOMING_SORT_KEY) {
		return sortBookingsUpcomingFirst(items);
	}

	if (!dateFilter && sortKey === "config.timestamp" && timezone) {
		return sortBookingsTodayFirst(items, timezone, sortDirection);
	}

	return [...items].sort((a, b) => {
		const cmp = compareBookingField(a, b, sortKey);
		if (cmp === 0) return 0;
		return sortDirection === -1 ? -cmp : cmp;
	});
}

const FILTER_PRESETS: Array<{ id: BookingDatePreset; label: string }> = [
	{ id: "today", label: "Today" },
	{ id: "tomorrow", label: "Tomorrow" },
	{ id: "next7", label: "Next 7 Days" },
	{ id: "all", label: "All Time" },
	{ id: "custom", label: "Custom" },
];

const EXPORT_PRESETS: Array<{ id: BookingDatePreset; label: string }> = [
	{ id: "today", label: "Today" },
	{ id: "tomorrow", label: "Tomorrow" },
	{ id: "next7", label: "Next 7 Days" },
	{ id: "all", label: "All Time" },
];

interface FilterProps {
	mode?: "filter";
	region: Region;
	preset: BookingDatePreset;
	start?: number;
	end?: number;
	onPresetChange: (preset: BookingDatePreset, range?: { start: number; end: number }) => void;
	onCustomRangeChange: (start: number, end: number) => void;
}

interface ExportProps {
	mode: "export";
	region: Region;
	preset: BookingDatePreset;
	start: number;
	end: number;
	onPresetChange: (preset: BookingDatePreset, range: { start: number; end: number }) => void;
	onRangeChange: (start: number, end: number) => void;
}

type Props = FilterProps | ExportProps;

/** Booking date presets + date range picker (filter or export). */
export class BookingsDateRangeFilter extends React.Component<Props> {
	render() {
		const { region, preset, start, end } = this.props;
		const isExport = this.props.mode === "export";
		const presets = isExport ? EXPORT_PRESETS : FILTER_PRESETS;
		const activeRange =
			start && end
				? { start, end }
				: bookingDateRangeForPreset("today", region.timezone);

		return (
			<div>
				<div className="flex-line flex-wrap" style={{ gap: "0.5rem" }}>
					{presets.map((p) => (
						<Button
							key={p.id}
							color={preset === p.id ? "primary" : "primary-inverse"}
							size="xxs"
							onClick={() => {
								if (p.id === "all") {
									if (isExport) {
										this.props.onPresetChange(
											"all",
											bookingExportAllTimeRange(region.timezone)
										);
									} else {
										this.props.onPresetChange("all");
									}
									return;
								}
								const range = bookingDateRangeForPreset(
									p.id,
									region.timezone,
									activeRange
								);
								if (range.start != null && range.end != null) {
									if (isExport) {
										this.props.onPresetChange(p.id, {
											start: range.start,
											end: range.end,
										});
									} else if (p.id === "custom") {
										this.props.onPresetChange("custom", {
											start: range.start,
											end: range.end,
										});
									} else {
										this.props.onPresetChange(p.id, {
											start: range.start,
											end: range.end,
										});
									}
								}
							}}
						>
							{p.label}
						</Button>
					))}
				</div>

				{(isExport || preset === "custom") &&
					activeRange.start != null &&
					activeRange.end != null && (
					<div className="m-t-3">
						<DateRangePicker
							formats={region.formats}
							timezone={region.timezone}
							start={activeRange.start}
							end={activeRange.end}
							allowPastDates={true}
							onSelect={(type, timestamp) => {
								const rangeStart = activeRange.start!;
								const rangeEnd = activeRange.end!;
								if (isExport) {
									if (type === "start") {
										this.props.onRangeChange(timestamp, rangeEnd);
									} else {
										this.props.onRangeChange(rangeStart, timestamp);
									}
								} else if (type === "start") {
									this.props.onCustomRangeChange(timestamp, rangeEnd);
								} else {
									this.props.onCustomRangeChange(rangeStart, timestamp);
								}
							}}
						/>
					</div>
				)}
			</div>
		);
	}
}
